/** @jsxImportSource solid-js */

import * as CDM from 'trimble-connect-sdk';
import {
  Component,
  Show,
  For,
  createSignal,
  createEffect,
  createMemo,
  onCleanup,
  on,
  batch,
} from 'solid-js';
import { ITab, IFilesFromConnectProps } from './types';
import { defineSolidEmpty, defineSolidAuthImage } from '@tcweb/connect-ui';
import {
  useProjectRegion,
  useTCPSService,
  FolderEntry,
  FolderInfo,
} from '@tc-attachment-picker/providers';
import { translate } from '@fulcrum/lib';

// Define web components at module load time
defineSolidEmpty();
defineSolidAuthImage();

interface IFilesFromConnectComponentProps {
  readonly isLoading: boolean;
  readonly totalCount: number;
  readonly subFolderList: FolderEntry[];
  readonly selectedRows: number[];
  readonly currentFolder?: FolderInfo;
  readonly previousFolder?: FolderInfo;
  readonly projectName: string;
  onSelectFolder(index: number): void;
  onSelect(index: number, action: string): void;
  onFolderBack(): void;
  onScroll(e: Event): void;
  readonly disableMultiSelect?: boolean;
  readonly allowedFileExtensions?: string[];
  readonly allowedFileNames?: string[];
  readonly is3DEmbbedded?: boolean;
  readonly accessToken?: string;
}

const FilesFromConnectComponent: Component<IFilesFromConnectComponentProps> = (
  props
) => {
  const getFileExtension = (filename: string): string => {
    return filename.split('.').pop()?.toLowerCase() || '';
  };

  /**
   * Checks if a folder entry (file or folder) is allowed to be selected.
   * Folders are always allowed. Files are checked against allowedFileExtensions.
   * @param entry - The folder entry (can be a file or folder)
   * @returns true if the entry is allowed, false otherwise
   */
  const isEntryAllowed = (entry: FolderEntry): boolean => {
    if (entry.type !== 'FILE') return true;
    if (!props.allowedFileExtensions) return true;
    const fileExt = getFileExtension(entry.name);
    return props.allowedFileExtensions.some(
      (ext) => ext.toLowerCase() === `.${fileExt}`
    );
  };

  const getFileExtensionsText = (): string => {
    if (
      !props.allowedFileExtensions ||
      props.allowedFileExtensions.length === 0
    )
      return '';

    const extensions = props.allowedFileExtensions;
    if (extensions.length === 1) {
      return extensions[0];
    }

    return [extensions.slice(0, -1).join(', '), extensions.slice(-1)[0]].join(
      ' and '
    );
  };

  const getFileNamesText = (): string => {
    if (!props.allowedFileNames || props.allowedFileNames.length === 0)
      return '';

    const names = props.allowedFileNames;
    if (names.length === 1) {
      return names[0];
    }

    return [names.slice(0, -1).join(', '), names.slice(-1)[0]].join(' and ');
  };

  return (
    <>
      <Show when={!props.disableMultiSelect}>
        {/* eslint-disable-next-line @typescript-eslint/naming-convention */}
        <small
          class="text-meta"
          // eslint-disable-next-line @typescript-eslint/naming-convention
          style={{ 'font-size': '0.75rem', 'font-weight': '400' }}
        >
          {translate('uploadMultiDescription')}
        </small>
      </Show>
      <br />
      <Show
        when={
          props.allowedFileExtensions && props.allowedFileExtensions.length > 0
        }
      >
        <small class="text-meta medium">
          {translate('TC.Common.AttchmentPickerFileTypes')}{' '}
          {getFileExtensionsText()}
        </small>
      </Show>
      <Show when={props.allowedFileNames && props.allowedFileNames.length > 0}>
        <small class="text-meta medium">
          Allowed file names: {getFileNamesText()}
        </small>
      </Show>

      <Show
        when={props.currentFolder && props.previousFolder}
        fallback={<p class="pt-1 text-ellipsis">{props.projectName}</p>}
      >
        <div class="group-items">
          <button
            class="button tertiary icon-circle icon-small dropdownpane-link"
            onClick={() => props.onFolderBack()}
          >
            <i class="icon-font tc-icon-arrow-line-back" />
          </button>
          <p class="text-ellipsis">{props.currentFolder?.name}</p>
        </div>
      </Show>

      <section>
        <div
          class={`bg-white ${
            !props.is3DEmbbedded ? 'search-result' : 'scroll-height'
          }`}
          onScroll={(e) => props.onScroll(e)}
        >
          <ul class="list">
            <For each={props.subFolderList}>
              {(entry, index) => {
                const allowed = isEntryAllowed(entry);
                const isSelected = createMemo(() =>
                  props.selectedRows.includes(index())
                );
                return (
                  <li
                    class={`list-item group selectable ${
                      allowed ? '' : 'disabled-list-item'
                    } ${isSelected() ? 'active' : ''}`}
                    style={
                      allowed ? undefined : 'pointer-events: none; opacity: 0.5'
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      if (entry.type === 'FOLDER') {
                        props.onSelectFolder(index());
                      } else {
                        props.onSelect(index(), 'single_selection');
                      }
                    }}
                  >
                    <Show
                      when={entry.type === 'FILE'}
                      fallback={
                        <div class="group-items">
                          <i class="icon-font tc-icon-folder" />
                          <span class="block text-ellipsis" title={entry.name}>
                            {entry.name}
                          </span>
                          <button class="button icon-circle icon-medium tertiary dropdownpane-link">
                            <i class="icon-font tc-icon-chevron-right" />
                          </button>
                        </div>
                      }
                    >
                      <div class="checkradio-group mb-0">
                        <div class="input-group checkradio">
                          <div class="group-items px-1 text-ellipsis">
                            <Show when={!props.disableMultiSelect}>
                              <div
                                class="icon checkbox"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  props.onSelect(index(), 'multi_selection');
                                }}
                              >
                                <input
                                  class="custom-input"
                                  name="filter"
                                  type="checkbox"
                                  checked={isSelected()}
                                />
                                <label />
                              </div>
                            </Show>
                            <div class="row-center text-ellipsis">
                              <Show
                                when={
                                  entry.thumbnailUrl && entry.thumbnailUrl[0]
                                }
                              >
                                {/* @ts-expect-error - tc-auth-image is a web component defined at runtime */}
                                <tc-auth-image
                                  src={entry.thumbnailUrl![0]}
                                  class="i16"
                                  access-token={props.accessToken}
                                />
                              </Show>
                              <div class="text-ellipsis" title={entry.name}>
                                {entry.name}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Show>
                  </li>
                );
              }}
            </For>
          </ul>
          <Show when={props.isLoading && props.subFolderList.length === 0}>
            <div class="center spinner-container" style={{ height: '200px' }}>
              <i class="icon-font tc-icon-spinner-dark" />
            </div>
          </Show>
          <Show when={!props.isLoading && props.totalCount === 0}>
            {/* @ts-expect-error - tc-empty is a web component defined at runtime */}
            <tc-empty
              message={translate('TCW.Common.NoSearchFound')}
              icon="tc-icon-folder"
              is-small-container={true}
            />
          </Show>
        </div>
      </section>
    </>
  );
};

const FilesFromConnectContainer: Component<IFilesFromConnectProps> = (
  props
) => {
  const projectRegion = useProjectRegion();
  const tcpsService = useTCPSService();

  const [isLoading, setIsLoading] = createSignal(false);
  const [totalCount, setTotalCount] = createSignal(0);
  const [subFolderList, setSubFolderList] = createSignal<FolderEntry[]>([]);
  const [selectedRows, setSelectedRows] = createSignal<number[]>([]);
  const [loadedCount, setLoadedCount] = createSignal(0);
  const [abortController, setAbortController] = createSignal<
    AbortController | undefined
  >();
  const [isInitialized, setIsInitialized] = createSignal(false);
  const [lastInitializedProject, setLastInitializedProject] =
    createSignal<string>();

  const currentFolder = (): FolderInfo | null =>
    tcpsService.data().currentFolder;
  const previousFolder = (): FolderInfo | null =>
    tcpsService.data().previousFolder;

  onCleanup(() => {
    const controller = abortController();
    if (controller) {
      controller.abort();
    }
  });

  const resetState = (resetInitialization = true): void => {
    // Use batch to group all state updates together
    batch(() => {
      setIsLoading(false);
      setTotalCount(0);
      setSubFolderList([]);
      setSelectedRows([]);
      setLoadedCount(0);
      setAbortController(undefined);

      if (resetInitialization) {
        setIsInitialized(false);
        setLastInitializedProject(undefined);
      }
    });
  };

  const initializeProject = async (): Promise<void> => {
    if (!props.projectId || !props.accessToken || !props.tcpsApiDomain) {
      return;
    }

    if (isLoading()) {
      return;
    }

    // Create a unique key for the current project configuration
    const projectKey = `${props.projectId}-${props.accessToken}-${props.tcpsApiDomain}`;

    // Skip initialization if already initialized for this project configuration
    if (isInitialized() && lastInitializedProject() === projectKey) {
      return;
    }

    try {
      await projectRegion.fetchRegionsAndProject(
        props.projectId,
        props.accessToken,
        props.tcpsApiDomain
      );

      const project = projectRegion.data().project;
      if (project) {
        await fetchRootFolderAndContents(project);
        setIsInitialized(true);
        setLastInitializedProject(projectKey);
      }
    } catch (error) {
      console.error('Failed to initialize FilesFromConnect:', error);
      resetState();
    }
  };

  const fetchRootFolderAndContents = async (
    project: CDM.Project
  ): Promise<void> => {
    try {
      const rootFolder = await tcpsService.fetchRootFolder(project);
      tcpsService.setCurrentFolder(rootFolder);

      if (rootFolder.id !== project.rootId && rootFolder.parentId) {
        const parentFolder = await tcpsService.fetchFolder(
          project,
          rootFolder.parentId
        );
        tcpsService.setPreviousFolder(parentFolder);
      } else {
        tcpsService.setPreviousFolder(null);
      }

      await switchFolder(project, rootFolder.id);
    } catch (error) {
      console.error('Failed to fetch root folder:', error);
    }
  };

  const switchFolder = async (
    project: CDM.Project,
    folderId: string,
    resetSelection = true
  ): Promise<void> => {
    const controller = abortController();
    if (controller) {
      controller.abort();
    }

    const newController = new AbortController();
    setAbortController(newController);

    try {
      setIsLoading(true);
      if (resetSelection) {
        setSelectedRows([]);
        setSubFolderList([]);
        setTotalCount(0);
        setLoadedCount(0);
      }

      const response = await tcpsService.fetchFolderContents(
        project,
        folderId,
        {
          offset: 0,
          limit: 25,
        }
      );

      setSubFolderList(response.items);
      setTotalCount(response.totalCount);
      setLoadedCount(response.items.length);
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Failed to fetch folder contents:', error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreFolders = async (): Promise<void> => {
    const project = projectRegion.data().project;
    const currentFolder = tcpsService.data().currentFolder;

    if (!project || !currentFolder || isLoading()) {
      return;
    }

    try {
      setIsLoading(true);

      const response = await tcpsService.fetchFolderContents(
        project,
        currentFolder.id,
        {
          offset: loadedCount(),
          limit: 25,
        }
      );

      setSubFolderList((prev) => [...prev, ...response.items]);
      setLoadedCount((prev) => prev + response.items.length);
    } catch (error) {
      console.error('Failed to load more folders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const emptyFileList = (): void => {
    // Use batch to group state updates and prevent cascading re-renders
    batch(() => {
      setSelectedRows([]);
      props.onFilesSubmit({
        selectedObject: [],
        type: ITab.FileFromConnect,
        source: 'attachment-picker',
      });
    });
  };

  const onSelectFolder = async (index: number): Promise<void> => {
    const folder = subFolderList()[index];
    const project = projectRegion.data().project;

    if (!folder || folder.type !== 'FOLDER' || !project) return;

    const currentFolder = tcpsService.data().currentFolder;
    if (currentFolder) {
      tcpsService.setPreviousFolder(currentFolder);
    }

    const newFolder: FolderInfo = {
      id: folder.id,
      name: folder.name,
      parentId: folder.parentId,
    };

    tcpsService.setCurrentFolder(newFolder);
    emptyFileList();
    await switchFolder(project, folder.id);
  };

  const onSelect = (index: number, action: string): void => {
    const folder = subFolderList()[index];
    if (!folder || folder.type !== 'FILE') return;

    let newSelectedRows: number[];

    if (action === 'single_selection') {
      // For single selection, replace the selection (don't append)
      newSelectedRows = [index];
    } else {
      // For multi-selection (checkbox), toggle the item
      if (selectedRows().includes(index)) {
        newSelectedRows = selectedRows().filter((i) => i !== index);
      } else {
        newSelectedRows = [...selectedRows(), index];
      }
    }

    // Use batch to group state updates and prevent cascading re-renders
    batch(() => {
      setSelectedRows(newSelectedRows);
      const selectedFiles = newSelectedRows
        .map((i) => subFolderList()[i])
        .filter(Boolean);

      props.onFilesSubmit({
        selectedObject: selectedFiles,
        type: ITab.FileFromConnect,
        source: 'attachment-picker',
      });
    });
  };

  const onFolderBack = async (): Promise<void> => {
    const previousFolder = tcpsService.data().previousFolder;
    const project = projectRegion.data().project;

    if (!previousFolder || !project) return;

    tcpsService.setCurrentFolder(previousFolder);
    emptyFileList();

    if (previousFolder.id !== project.rootId && previousFolder.parentId) {
      try {
        const newPreviousFolder = await tcpsService.fetchFolder(
          project,
          previousFolder.parentId
        );
        tcpsService.setPreviousFolder(newPreviousFolder);
      } catch {
        tcpsService.setPreviousFolder(null);
      }
    } else {
      tcpsService.setPreviousFolder(null);
    }

    await switchFolder(project, previousFolder.id);
  };

  const onScroll = (e: Event): void => {
    const target = e.currentTarget as HTMLDivElement;
    const scrollHeight = Math.round(target.offsetHeight + target.scrollTop);
    const divHeight = target.scrollHeight;

    if (scrollHeight < divHeight) {
      return;
    }

    if (totalCount() > loadedCount()) {
      loadMoreFolders();
    }
  };

  // Create memos for stable references
  const isTabActive = createMemo(
    () => props.tab === ITab.FileFromConnect && props.isShowFileFromConnect
  );

  const projectKey = createMemo(
    () =>
      `${props.projectId || ''}-${props.accessToken || ''}-${
        props.tcpsApiDomain || ''
      }`
  );

  const shouldInitialize = createMemo(
    () =>
      isTabActive() &&
      props.projectId &&
      props.accessToken &&
      props.tcpsApiDomain &&
      (!isInitialized() || lastInitializedProject() !== projectKey())
  );

  // Reset selected rows when tab changes
  createEffect(
    on(
      () => props.tab,
      (currentTab) => {
        if (currentTab !== ITab.FileFromConnect) {
          setSelectedRows([]);
        }
      }
    )
  );

  // Reset state when tab becomes inactive
  createEffect(
    on(isTabActive, (active) => {
      if (!active) {
        resetState(true); // Reset initialization so component reinitializes when tab becomes active again
      }
    })
  );

  // Reset state when project parameters change
  createEffect(
    on(projectKey, (newProjectKey, oldProjectKey) => {
      if (oldProjectKey && oldProjectKey !== newProjectKey) {
        setIsInitialized(false);
        setLastInitializedProject(undefined);
      }
    })
  );

  // Initialize project when conditions are met
  createEffect(
    on(shouldInitialize, (should) => {
      if (should) {
        initializeProject();
      }
    })
  );

  // Create memoized computations to prevent unnecessary re-calculations
  const missingProps = createMemo(() => {
    const missing = [];
    if (!props.projectId) missing.push('projectId');
    if (!props.accessToken) missing.push('accessToken');
    if (!props.tcpsApiDomain) missing.push('tcpsApiDomain');
    return missing;
  });

  const hasRequiredProps = createMemo(() => missingProps().length === 0);

  return (
    <Show
      when={props.tab === ITab.FileFromConnect && props.isShowFileFromConnect}
    >
      <Show
        when={hasRequiredProps()}
        fallback={
          <div class="error-container p-3">
            <div class="error-message">
              <i class="icon-font tc-icon-warning text-warning mr-2" />
              <strong>Configuration Error:</strong> FilesFromConnect requires
              the following props to be provided to AttachmentPicker:
              <ul class="mt-2">
                <For each={missingProps()}>
                  {(prop) => (
                    <li>
                      <code>{prop}</code>
                    </li>
                  )}
                </For>
              </ul>
              <p class="mt-2 text-muted">
                Please provide these props for FILE_FROM_CONNECT functionality
                to work.
              </p>
            </div>
          </div>
        }
      >
        <FilesFromConnectComponent
          isLoading={isLoading()}
          totalCount={totalCount()}
          subFolderList={subFolderList()}
          selectedRows={selectedRows()}
          currentFolder={currentFolder() || undefined}
          previousFolder={previousFolder() || undefined}
          projectName={props.projectName}
          onSelectFolder={onSelectFolder}
          onSelect={onSelect}
          onFolderBack={onFolderBack}
          onScroll={onScroll}
          disableMultiSelect={props.disableMultiSelect}
          allowedFileExtensions={props.allowedFileExtensions}
          allowedFileNames={props.allowedFileNames}
          is3DEmbbedded={props.is3DEmbbedded}
          accessToken={props.accessToken}
        />
      </Show>
    </Show>
  );
};

export default FilesFromConnectContainer;
